import { useEffect, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { useNavigate, useParams } from "react-router-dom";
import { Button, Spin } from "antd";
import { WarningOutlined } from "@ant-design/icons";
import { getYieldGapsReport } from "@services/api";
import {
  tableLegendHelper,
  tableYieldGapLegendHelper,
} from "@/components/reports/yield_gap/yieldGapReportHelper";
import { FieldMarkerIcon } from "@/components/map/utils/customs";
import { toolbarBaseControls } from "@/features/map/utils/configs";
import AgroMapFarm from "@/features/map/presentation/components/AgroMapFarm";
import line from "@assets/images/yield_gap_report_images/Line.svg";
import table from "@assets/images/yield_gap_report_images/TABLE.svg";
import shape1 from "@assets/images/yield_gap_report_images/shape1.png";
import circle from "@assets/images/yield_gap_report_images/circle.png";
import circlebot from "@assets/images/yield_gap_report_images/circlebot.png";
import frame2182 from "@assets/images/yield_gap_report_images/Frame2182.png";
import frame2173 from "@assets/images/yield_gap_report_images/Frame2173.png";
import headerImage from "@assets/images/yield_gap_report_images/headerImage.png";
import footerImage from "@assets/images/yield_gap_report_images/footerImage.png";
import footerTitle from "@assets/images/yield_gap_report_images/footerTitle.png";
import agroyaarPlatform from "@assets/images/yield_gap_report_images/agroyaarPlatform.png";

function YieldGapReport() {
  const { id } = useParams();
  const yieldReportQuery = useQuery(["yieldReportQuery", id], () =>
    getYieldGapsReport(id)
  );
  const [farmerInformation, setFarmerInformation] = useState({});
  const [farmInformation, setFarmInformation] = useState({});
  const [abstractReport, setabstractReport] = useState([]);
  const [categoriesYieldGapContribution, setCategoriesYieldGapContribution] =
    useState({});
  const [yieldGapValue, setYieldGapValue] = useState({});
  const [lostEconomicValueAmount, setLostEconomicValueAmount] = useState({});
  const [parametersYieldGapContribution, setParametersYieldGapContribution] =
    useState([]);
  const [
    parametersYieldGapContributionChart,
    setParametersYieldGapContributionChart,
  ] = useState([]);
  const navigate = useNavigate();

  useEffect(() => {
    if (yieldReportQuery?.data) {
      setFarmerInformation(
        yieldReportQuery?.data?.yieldGap?.report?.farmerInformation
      );
      setFarmInformation(
        yieldReportQuery?.data?.yieldGap?.report?.farmInformation
      );
      setabstractReport(
        yieldReportQuery?.data?.yieldGap?.report?.abstractReport
      );
      setCategoriesYieldGapContribution(
        yieldReportQuery?.data?.yieldGap?.report?.categoriesYieldGapContribution
      );
      setYieldGapValue(yieldReportQuery?.data?.yieldGap?.report?.yieldGapValue);
      setLostEconomicValueAmount(
        yieldReportQuery?.data?.yieldGap?.report?.lostEconomicValueAmount
      );
      setParametersYieldGapContribution(
        yieldReportQuery?.data?.yieldGap?.report?.parametersYieldGapContribution
      );
      setParametersYieldGapContributionChart(
        yieldReportQuery?.data?.yieldGap?.report
          ?.parametersYieldGapContributionChart
      );
    }
  }, [yieldReportQuery?.data]);

  // logic of page 3
  if (abstractReport[0]?.length < abstractReport[1]?.length) {
    while (abstractReport[0]?.length < abstractReport[1]?.length) {
      abstractReport[0]?.push({ title: "", unit: "", value: "" });
    }
  } else if (abstractReport[1]?.length < abstractReport[0]?.length) {
    while (abstractReport[1]?.length < abstractReport[0]?.length) {
      abstractReport[1]?.push({ title: "", unit: "", value: "" });
    }
  }

  // page number
  let pageNumber = 0;

  // logic of page 4
  let newHeightBarPage4 = [];
  categoriesYieldGapContribution?.barChart?.y?.forEach((y) =>
    newHeightBarPage4.push(
      (y * 300) /
        categoriesYieldGapContribution?.barChart?.labels[
          categoriesYieldGapContribution?.barChart?.labels?.length - 1
        ]
    )
  );
  // generating new y for pieces of bar height

  // declaring variables in page 5
  let newHeightBarPage5;

  // declaring variables in page 6
  let newHeightBarPage6;

  // declaring variables in page 8
  let sumHeightPage8, newHeightBarPage8;

  // logic of page 7
  if (
    parametersYieldGapContribution[0]?.length <
    parametersYieldGapContribution[1]?.length
  ) {
    while (
      parametersYieldGapContribution[0]?.length <
      parametersYieldGapContribution[1]?.length
    ) {
      parametersYieldGapContribution[0]?.push({ title: "", value: "" });
    }
  } else if (
    parametersYieldGapContribution[1]?.length <
    parametersYieldGapContribution[0]?.length
  ) {
    while (
      parametersYieldGapContribution[1]?.length <
      parametersYieldGapContribution[0]?.length
    ) {
      parametersYieldGapContribution[1]?.push({ title: "", value: "" });
    }
  }

  //declaring variables for dynamic pages in structure of page 8
  let zeroYvalueLegend = [];

  return (
    <>
      {yieldReportQuery?.isLoading ? (
        <div className="flex flex-row items-center w-[500px] h-[500px] mx-auto my-[6rem]">
          <Spin
            className="spiner-loading-farm-management"
            style={{ margin: "auto" }}
            size="large"
          />
        </div>
      ) : (
        <div className="section-container flex flex-col items-center justify-center w-[720px] mx-auto !bg-white">
          {/* Page1 (Introduction) */}
          <div className="page1">
            <div className="flex flex-col justify-between items-center w-full h-[1048px]">
              <img
                className="header"
                src={headerImage}
                alt="header"
                width="750px"
              />

              <div className="flex flex-col justify-between items-center w-full gap-9 mt-[-90px]">
                <p
                  className="text-[#222] text-[36px] font-bold !font-[B-Nazanin]"
                  style={{ textShadow: "0px 2px 4px rgba(0, 0, 0, 0.25)" }}
                >
                  کشاورزی{" "}
                  <span className="text-president !font-[B-Nazanin]">
                    مطمئن‌
                    <span className="text-[#222] !font-[B-Nazanin]">تر، </span>
                  </span>
                  <span className="text-president !font-[B-Nazanin]">
                    دقیق‌
                    <span className="text-[#222] !font-[B-Nazanin]">تر و </span>
                  </span>
                  <span className="text-president !font-[B-Nazanin]">
                    اقتصادی‌
                    <span className="text-[#222] !font-[B-Nazanin]">تر</span>
                  </span>
                </p>

                <img
                  src={frame2173}
                  className="w-[420px] !font-[B-Nazanin]"
                  alt="chart"
                />

                <span
                  className="text-[#5700B4] font-bold text-[64px] !font-[B-Nazanin]"
                  style={{
                    textShadow: "1px 0px 3px rgba(118, 118, 118, 0.61)",
                  }}
                >
                  گزارش خلأ عملکرد
                </span>

                <p className="text-[#000] font-semibold text-[24px] whitespace-nowrap !font-[B-Nazanin]">
                  هدف ما تصمیم‌گیری برای کشاورز نیست، کمک به تصمیم‌گیری بهتر
                  کشاورز است
                </p>

                <p className="text-president font-bold text-[28px] tracking-[4px]">
                  WWW.AGROYAAR.COM
                </p>
              </div>

              <div className="footer w-full flex flex-col justify-start items-center relative">
                <img
                  className="absolute bottom-0 z-[2]"
                  src={footerImage}
                  width="750px"
                  alt="footer"
                />
                <img
                  className="absolute bottom-2 left-[30px] z-[3]"
                  src={footerTitle}
                  alt="footer-title"
                />
                <span className="absolute bottom-1 left-[60px] z-[3] text-[12px] font-bold text-[#fff] tracking-[9px] text-center">
                  WWW.AGROYAAR.COM
                </span>
                <span className="absolute bottom-[5px] right-[15px] z-[3] text-[18px] font-semibold text-[#fff]">
                  {++pageNumber}
                </span>
              </div>
            </div>
          </div>

          {/* Page2 (Description) */}
          <div className="page2">
            <div className="flex flex-col justify-between items-center w-full h-[1048px]">
              <img
                className="header"
                src={frame2182}
                alt="header"
                width="750px"
              />

              <div className="flex flex-row-reverse justify-center items-center w-full gap-5 mt-[-30px] px-2">
                <div className="flex flex-col justify-center items-end w-[50%] gap-2">
                  <div className="flex flex-row justify-end items-center gap-[19px] w-full"></div>
                </div>

                <div className="flex flex-col justify-center items-start w-[50%] gap-2">
                  <span className="font-bold text-[20px] text-[#222] !font-[B-Nazanin]">
                    اطلاعات کشاورز
                  </span>

                  <div className="flex flex-row-reverse justify-end items-center gap-[19px] w-full">
                    <div className="text-agrotext font-bold text-[18px] flex flex-col justify-center items-start gap-[6px]">
                      <span className="!font-[B-Nazanin]">
                        {farmerInformation?.firstName}{" "}
                        {farmerInformation?.lastName}
                      </span>

                      <div className="flex flex-row items-start gap-2">
                        <span className="!font-[B-Nazanin]">کد کشاورزی:</span>
                        <span className="!font-sans text-[16px]">
                          {farmerInformation?.farmerCode}
                        </span>
                      </div>

                      <div className="flex flex-row items-start gap-2">
                        <span className="!font-[B-Nazanin]">استان و شهر:</span>
                        <span className="!font-[B-Nazanin]">
                          {farmerInformation?.province?.name}،{" "}
                          {farmerInformation?.city?.name}
                        </span>
                      </div>
                    </div>

                    <img
                      src={
                        process.env.REACT_APP_ARVAN_S3_BASE_URL +
                        farmerInformation?.avatar
                      }
                      alt="profile"
                      width="70px"
                      height="70px"
                      className="rounded-full border-[2px] border-solid border-president"
                    />
                  </div>
                </div>
              </div>

              <img
                src={line}
                alt="line"
                width="750px"
                className="mt-[-50px] mr-0 mb-[-30px] ml-0"
              />

              <div className="flex flex-row justify-center items-end w-full gap-7 px-2">
                <div className="flex flex-col justify-center items-start w-[50%] gap-2">
                  <span className="font-bold text-[20px] text-[#222] !font-[B-Nazanin]">
                    اطلاعات زمین
                  </span>

                  <div className="flex flex-row-reverse justify-end items-center gap-[19px] w-full">
                    <div className="text-agrotext font-bold text-[18px] flex flex-col justify-center items-start gap-[6px]">
                      <div className="flex flex-row items-start gap-2">
                        <span className="!font-[B-Nazanin]">نام قطعه:</span>
                        <span className="!font-[B-Nazanin]">
                          {farmInformation?.name}
                        </span>
                      </div>

                      <div className="flex flex-row items-start gap-2">
                        <span className="!font-[B-Nazanin]">مساحت زمین:</span>
                        <span className="!font-[B-Nazanin]">
                          {farmInformation?.area}هکتار
                        </span>
                      </div>

                      <div className="flex flex-row items-start gap-2">
                        <span className="!font-[B-Nazanin]">استان و شهر:</span>
                        <span className="!font-[B-Nazanin]">
                          {farmInformation?.province?.name} ، {farmInformation?.city?.name}
                        </span>
                      </div>
                    </div>

                    <div className="relative w-[80px] h-[80px] overflow-clip rounded-[999px]">
                      <div className="THISISFORMAPLOCK! bg-transparent z-[99999999] w-full h-full absolute"></div>
                      <AgroMapFarm
                        mapId={
                          yieldReportQuery?.data?.yieldGap?.report
                            ?.farmInformation?.id
                        }
                        type="onlyShow"
                        toolbarOptions={toolbarBaseControls}
                        markerIcon={FieldMarkerIcon}
                        currentFarmPolygon={
                          yieldReportQuery?.data?.yieldGap?.report
                            ?.farmInformation?.polygon
                        }
                      />
                    </div>
                  </div>
                </div>

                <div className="flex flex-col justify-center items-end w-[50%] gap-2">
                  <div className="flex flex-row-reverse justify-end items-center gap-[19px] w-full">
                    <div className="text-agrotext font-bold text-[18px] flex flex-col justify-center items-start gap-[6px]">
                      <div className="flex flex-row items-start gap-2">
                        <span className="!font-[B-Nazanin]">نام محصول:</span>
                        <span className="!font-[B-Nazanin]">
                          {farmInformation?.product?.persianName}
                        </span>
                      </div>

                      <div className="flex flex-row items-start gap-2 opacity-0">
                        <span className="!font-[B-Nazanin]">نام رقم:</span>
                        <span className="!font-[B-Nazanin]">
                          قرمز مایل به زرد
                        </span>
                      </div>

                      <div className="flex flex-row items-start gap-2">
                        <span className="!font-[B-Nazanin]">
                          نقطه <span className="text-[16px]">GPS</span> زمین:
                        </span>
                        {farmInformation?.centerCoordinates && (
                          <span className="!font-[B-Nazanin]">
                            {parseFloat(
                              farmInformation?.centerCoordinates[1]?.toFixed(4)
                            )}{" "}
                            ،{" "}
                            {parseFloat(
                              farmInformation?.centerCoordinates[0]?.toFixed(4)
                            )}
                          </span>
                        )}
                      </div>
                    </div>

                    <img
                      src={farmInformation?.product?.imageUrl}
                      alt="product-name"
                      width="80px"
                      height="80px"
                      className="rounded-full"
                    />
                  </div>
                </div>
              </div>

              <div className="px-2">
                <div className="flex flex-col justify-start items-start border-[3px] border-dashed border-president rounded-[20px] p-[10px]">
                  <span className="font-bold text-[#202020] text-[22px] !font-[B-Nazanin]">
                    کشاورز عزیز
                  </span>

                  <ul className="leading-[25px] font-semibold text-[16px]">
                    <li className="!font-[B-Nazanin]">
                      گزارش خلأ عملکرد{" "}
                      <span className="text-president">
                        {farmInformation?.productName}
                      </span>{" "}
                      در این قطعه، بر اساس اطلاعات ثبت شده از مزرعه شما تهیه شده
                      و نتایج این گزارش وابسته به صحت داده‌های ورودی شما است.
                    </li>

                    <li className="!font-[B-Nazanin]">
                      در این گزارش با توجه به مدیریت فعلی قطعه، مهمترین دلایل
                      کاهش عملکرد و سهم هر عامل به دست آمده است. در صورت اصلاح
                      مدیریت این عوامل میتوان شاهد افزایش عملکرد بود.
                    </li>

                    <li className="!font-[B-Nazanin]">
                      دقت داشته باشید عوامل مدیریتی و محیطی زیادی برای رسیدن به
                      عملکرد مطلوب نقش دارند. مدیریت تمامی فاکتورهای خلأ عملکرد
                      در کنار هم می‌تواند شما را به عملکرد مطلوب نزدیک کند.
                    </li>
                  </ul>
                </div>
              </div>

              <div className="flex flex-row justify-between items-start w-full mb-[80px] px-2">
                <div className="flex flex-col justify-start leading-6 w-[70%]">
                  <span className="font-bold text-[22px] !font-[B-Nazanin]">
                    تعاریف
                  </span>

                  <span className="font-bold text-[18px] !font-[B-Nazanin]">
                    {" "}
                    عملکرد واقعی چیست؟
                  </span>

                  <span className="font-semibold text-[16px] !font-[B-Nazanin]">
                    عملکردی که در مزرعه بدست آورده‌اید را عملکرد واقعی می‌گویند.
                  </span>

                  <span className="font-bold text-[18px] !font-[B-Nazanin]">
                    {" "}
                    پتانسیل عملکرد چیست؟{" "}
                  </span>

                  <span className="font-semibold text-[16px] !font-[B-Nazanin]">
                    بالاترین عملکردی که با مدیریت مطلوب تمام عوامل قابل مدیریت
                    در مزرعه می‌توانیم به دست آوریم را پتانسیل عملکرد می‌گویند.
                  </span>

                  <span className="font-bold text-[18px] !font-[B-Nazanin]">
                    خلأ عملکرد چیست؟
                  </span>

                  <span className="font-semibold text-[16px] !font-[B-Nazanin]">
                    به فاصله بین عملکرد واقعی با پتانسیل عملکرد، خلأ عملکرد
                    (شکاف عملکرد) گفته می‌شود. در واقع، این فاصله به ما نشان
                    می‌دهد چه مقدار تولید کرده‌ایم و چه ظرفیتی برای تولید
                    داشته‌ایم.
                  </span>
                </div>

                <img src={shape1} alt="shape 1" width="250px" height="250px" />
              </div>

              <div className="footer w-full flex flex-col justify-start items-center relative bottom-0">
                <img
                  className="absolute bottom-0 z-[2]"
                  src={footerImage}
                  width="750px"
                  alt="footer"
                />
                <img
                  className="absolute bottom-2 left-[30px] z-[3]"
                  src={agroyaarPlatform}
                  alt="footer"
                />
                <span className="absolute bottom-[5px] left-[30px] z-[3] text-[12px] font-bold text-[#fff] !font-sans">
                  کد گزارش: {yieldReportQuery?.data?.yieldGap?.yieldGapCode}
                </span>
                <span className="absolute bottom-[5px] right-[15px] z-[3] text-[18px] font-semibold text-[#fff]">
                  {++pageNumber}
                </span>
              </div>
            </div>
          </div>

          {/* <!-- Page3 --> */}
          <div className="page3">
            <section className="flex flex-col justify-between items-center w-full h-[1048px]">
              <img
                className="header"
                src={frame2182}
                alt="header"
                width="720px"
              />

              <div className="flex flex-col justify-start items-center h-full w-full gap-[10px] px-2">
                <div className="flex flex-col justify-center items-start w-full">
                  <span className="font-bold text-[22px] text-[#222] !font-[B-Nazanin]">
                    چکیده گزارش
                  </span>
                  <span className="font-bold text-[16px] text-[#222] !font-[B-Nazanin]">
                    جدول 1- وضعیت خلأ عملکرد و ارزش اقتصادی
                  </span>
                </div>

                <div className="flex flex-row justify-center items-center">
                  <div className="flex flex-col justify-center items-center">
                    <div className="flex flex-row justify-center items-center h-full w-full">
                      <div
                        className="w-[160px] h-[25px] leading-6 text-center text-[16px] !font-[B-Nazanin] font-bold text-president"
                        style={{
                          borderTop: "1.5px solid black",
                          borderRight: "1.5px solid black",
                          borderBottom: "0.75px solid black",
                          borderLeft: "0.75px solid black",
                          borderTopRightRadius: "5px",
                          backgroundColor: "rgba(70, 15, 129, 0.20)",
                        }}
                      >
                        پارامتر‌های تولید
                      </div>
                      <div
                        className="w-[90px] h-[25px] leading-6 text-center !font-[B-Nazanin] text-president text-[16px] font-bold"
                        style={{
                          borderTop: "1.5px solid black",
                          borderRight: "0.75px solid black",
                          borderBottom: "0.75px solid black",
                          borderLeft: "0.75px solid black",
                          backgroundColor: "rgba(70, 15, 129, 0.20)",
                        }}
                      >
                        مقدار
                      </div>
                      <div
                        className="w-[90px] h-[25px] leading-6 text-center !font-[B-Nazanin] text-president text-[16px] font-bold"
                        style={{
                          borderTop: "1.5px solid black",
                          borderRight: "0.75px solid black",
                          borderBottom: "0.75px solid black",
                          borderLeft: "0.75px solid black",
                          backgroundColor: "rgba(70, 15, 129, 0.20)",
                        }}
                      >
                        واحد
                      </div>
                    </div>

                    {abstractReport[0]?.map((item, i) => {
                      const cell1Style = {
                        borderBottomRightRadius:
                          i === abstractReport[0].length - 1 && "5px",
                        borderTop: "0.75px solid black",
                        borderRight: "1.5px solid black",
                        borderBottom:
                          i === abstractReport[0].length - 1
                            ? "1.5px solid black"
                            : "0.75px solid black",
                        borderLeft: "0.75px solid black",
                      };
                      const cell2Style = {
                        backgroundColor:
                          (item?.title?.includes("خلأ تولید") ||
                            item?.title?.includes("از دست رفته")) &&
                          "#F00",
                        borderTop: "0.75px solid black",
                        borderRight: "0.75px solid black",
                        borderBottom:
                          i === abstractReport[0].length - 1
                            ? "1.5px solid black"
                            : "0.75px solid black",
                        borderLeft: "0.75px solid black",
                      };
                      const cell3Style = {
                        borderTop: "0.75px solid black",
                        borderRight: "0.75px solid black",
                        borderBottom:
                          i === abstractReport[0].length - 1
                            ? "1.5px solid black"
                            : "0.75px solid black",
                        borderLeft: "0.75px solid black",
                      };
                      return (
                        <div
                          key={i}
                          className="flex flex-row justify-center items-center h-full w-full"
                        >
                          <div
                            className="w-[160px] h-[25px] leading-6 text-center !font-[B-Nazanin] text-[#333] text-[14px] font-bold"
                            style={cell1Style}
                          >
                            {item?.title}
                          </div>
                          <div
                            className="w-[90px] h-[25px] leading-6 text-center !font-[B-Nazanin] text-[#333] text-[16px] font-bold"
                            style={cell2Style}
                          >
                            {item?.value}
                          </div>
                          <div
                            className="w-[90px] h-[25px] leading-6 text-center !font-[B-Nazanin] text-president text-[16px] font-bold"
                            style={cell3Style}
                          >
                            {item?.unit}
                          </div>
                        </div>
                      );
                    })}
                  </div>

                  <div className="flex flex-col justify-center items-center">
                    <div className="flex flex-row justify-center items-center h-full w-full">
                      <div
                        className="w-[180px] h-[25px] leading-6 text-center !font-[B-Nazanin] text-president text-[16px] font-bold"
                        style={{
                          borderTop: "1.5px solid black",
                          borderRight: "0.75px solid black",
                          borderBottom: "0.75px solid black",
                          borderLeft: "0.75px solid black",
                          backgroundColor: "rgba(70, 15, 129, 0.20)",
                        }}
                      >
                        پارامتر‌های تولید
                      </div>
                      <div
                        className="w-[95px] h-[25px] leading-6 text-center !font-[B-Nazanin] text-president text-[16px] font-bold"
                        style={{
                          borderTop: "1.5px solid black",
                          borderRight: "0.75px solid black",
                          borderBottom: "0.75px solid black",
                          borderLeft: "0.75px solid black",
                          backgroundColor: "rgba(70, 15, 129, 0.20)",
                        }}
                      >
                        مقدار
                      </div>
                      <div
                        className="w-[85px] h-[25px] leading-6 text-center !font-[B-Nazanin] text-president text-[16px] font-bold"
                        style={{
                          borderTop: "1.5px solid black",
                          borderRight: "0.75px solid black",
                          borderBottom: "0.75px solid black",
                          borderLeft: "1.5px solid black",
                          borderTopLeftRadius: "5px",
                          backgroundColor: "rgba(70, 15, 129, 0.20)",
                        }}
                      >
                        واحد
                      </div>
                    </div>

                    {abstractReport[1]?.map((item, i) => {
                      const cell1Style = {
                        borderTop: "0.75px solid black",
                        borderRight: "0.75px solid black",
                        borderBottom:
                          i === abstractReport[1]?.length - 1
                            ? "1.5px solid black"
                            : "0.75px solid black",
                        borderLeft: "0.75px solid black",
                      };
                      const cell2Style = {
                        backgroundColor:
                          i === abstractReport[1]?.length - 1
                            ? tableLegendHelper(item?.value).color
                            : i === abstractReport[1].length - 2
                            ? tableLegendHelper(item?.value).color
                            : item?.title?.includes("از دست رفته")
                            ? "#F00"
                            : "",
                        borderTop: "0.75px solid black",
                        borderRight: "0.75px solid black",
                        borderBottom:
                          i === abstractReport[1]?.length - 1
                            ? "1.5px solid black"
                            : "0.75px solid black",
                        borderLeft: "0.75px solid black",
                      };
                      const cell3Style = {
                        borderBottomLeftRadius:
                          i === abstractReport[1]?.length - 1 && "5px",
                        borderTop: "0.75px solid black",
                        borderRight: "0.75px solid black",
                        borderBottom:
                          i === abstractReport[1]?.length - 1
                            ? "1.5px solid black"
                            : "0.75px solid black",
                        borderLeft: "1.5px solid black",
                      };
                      return (
                        <div
                          key={i}
                          className="flex flex-row justify-center items-center h-full w-full"
                        >
                          <div
                            className="w-[180px] h-[25px] leading-6 text-center !font-[B-Nazanin] text-[#333] text-[13px] font-bold"
                            style={cell1Style}
                          >
                            {item?.title}
                          </div>
                          <div
                            className="w-[95px] h-[25px] leading-6 text-center !font-[B-Nazanin] text-[#333] text-[16px] font-bold"
                            style={cell2Style}
                          >
                            {i === abstractReport[1]?.length - 1
                              ? tableLegendHelper(item?.value).label
                              : item?.unit?.includes("ریال در هکتار")
                              ? Math.round(item?.value)
                                  .toString()
                                  .replace(/\B(?=(\d{3})+(?!\d))/g, ",")
                              : Math.round(item?.value)}
                          </div>
                          <div
                            className="w-[85px] h-[25px] leading-6 text-center !font-[B-Nazanin] text-president text-[16px] font-bold"
                            style={cell3Style}
                          >
                            {item?.unit}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>

                <div className="flex flex-col justify-center items-start w-full mt-5 gap-[2px] px-2">
                  <span className="font-bold text-[24px] text-right text-[black] leading-[45px] !font-[B-Nazanin]">
                    راهنما
                  </span>
                  <span className="pr-[15px] !font-[B-Nazanin]">
                    <span className="font-semibold text-[18px] !font-[B-Nazanin]">
                      ارزش اقتصادی از دست رفته:{" "}
                    </span>
                    میزان کاهش درآمد به علت کاهش عملکرد می‌باشد.
                  </span>
                  <span className="pr-[15px] !font-[B-Nazanin]">
                    <span className="font-semibold text-[18px] !font-[B-Nazanin]">
                      راندمان مدیریت کشاورز:
                    </span>{" "}
                    بر اساس نحوه مدیریت قطعه توسط کشاورز تعیین شده و مقدار آن
                    بین صفر (مدیریت بسیار ضعیف) تا 100 (مدیریت عالی) می‌باشد.
                  </span>
                  <span className="pr-[15px] !font-[B-Nazanin]">
                    <span className="font-semibold text-[18px] !font-[B-Nazanin]">
                      رتبه مدیریت کشاورز:
                    </span>{" "}
                    بر اساس راندمان مدیریت کشاورز تعیین شده و در 9 سطح از بسیار
                    ضعیف تا عالی می‌باشد.
                  </span>
                </div>

                <div className="flex flex-col justify-center items-center mt-5">
                  <div className="flex flex-row justify-center items-center h-full w-full">
                    <div
                      className="w-[120px] h-[22px] leading-[20px] text-center text-president font-bold text-[16px] !font-[B-Nazanin]"
                      style={{
                        borderTop: "1.5px solid black",
                        borderRight: "1.5px solid black",
                        borderBottom: "0.75px solid black",
                        borderLeft: "0.75px solid black",
                        backgroundColor: "rgba(70, 15, 129, 0.20)",
                        borderTopRightRadius: "5px",
                      }}
                    >
                      رتبه مدیریت
                    </div>
                    <div
                      className="w-[120px] h-[22px] leading-[20px] text-center text-president text-[16px] font-bold !font-[B-Nazanin]"
                      style={{
                        borderTop: "1.5px solid black",
                        borderRight: "0.75px solid black",
                        borderBottom: "0.75px solid black",
                        borderLeft: "0.75px solid black",
                        backgroundColor: "rgba(70, 15, 129, 0.20)",
                      }}
                    >
                      راندمان مدیریت
                    </div>
                    <div
                      className="w-[120px] h-[22px] leading-[20px] text-center text-president text-[16px] font-bold !font-[B-Nazanin]"
                      style={{
                        borderTop: "1.5px solid black",
                        borderRight: "0.75px solid black",
                        borderBottom: "0.75px solid black",
                        borderLeft: "1.5px solid black",
                        backgroundColor: "rgba(70, 15, 129, 0.20)",
                        borderTopLeftRadius: "5px",
                      }}
                    >
                      طیف رنگی
                    </div>
                  </div>

                  <div className="flex flex-row justify-center items-center h-full w-full">
                    <div
                      className="w-[120px] h-[22px] leading-[20px] text-center text-[#333] text-[16px] font-bold !font-[B-Nazanin]"
                      style={{
                        borderTop: "0.75px solid black",
                        borderRight: "1.5px solid black",
                        borderBottom: "0.75px solid black",
                        borderLeft: "0.75px solid black",
                      }}
                    >
                      عالی
                    </div>
                    <div
                      className="w-[120px] h-[22px] leading-[20px] text-center text-[#333] text-[16px] font-bold !font-[B-Nazanin]"
                      style={{
                        borderTop: "0.75px solid black",
                        borderRight: "0.75px solid black",
                        borderBottom: "0.75px solid black",
                        borderLeft: "0.75px solid black",
                      }}
                    >
                      بیشتر از 90 درصد
                    </div>
                    <div
                      className="w-[120px] h-[22px] leading-[20px] text-center text-[#333] text-[16px] font-bold bg-agroyar"
                      style={{
                        borderTop: "0.75px solid black",
                        borderRight: "0.75px solid black",
                        borderBottom: "1.5px solid black",
                        borderLeft: "1.5px solid black",
                      }}
                    ></div>
                  </div>

                  <div className="flex flex-row justify-center items-center h-full w-full">
                    <div
                      className="w-[120px] h-[22px] leading-[20px] text-center text-[#333] text-[16px] font-bold !font-[B-Nazanin]"
                      style={{
                        borderTop: "0.75px solid black",
                        borderRight: "1.5px solid black",
                        borderBottom: "0.75px solid black",
                        borderLeft: "0.75px solid black",
                      }}
                    >
                      بسیار خوب
                    </div>
                    <div
                      className="w-[120px] h-[22px] leading-[20px] text-center text-[#333] text-[16px] font-bold !font-[B-Nazanin]"
                      style={{
                        borderTop: "0.75px solid black",
                        borderRight: "0.75px solid black",
                        borderBottom: "0.75px solid black",
                        borderLeft: "0.75px solid black",
                      }}
                    >
                      بین 80 تا 90 درصد
                    </div>
                    <div
                      className="w-[120px] h-[22px] leading-[20px] text-center text-[#333] text-[16px] font-bold bg-[#159900]"
                      style={{
                        borderTop: "0.75px solid black",
                        borderRight: "0.75px solid black",
                        borderBottom: "1.5px solid black",
                        borderLeft: "1.5px solid black",
                      }}
                    ></div>
                  </div>

                  <div className="flex flex-row justify-center items-center h-full w-full">
                    <div
                      className="w-[120px] h-[22px] leading-[20px] text-center text-[#333] text-[16px] font-bold !font-[B-Nazanin]"
                      style={{
                        borderTop: "0.75px solid black",
                        borderRight: "1.5px solid black",
                        borderBottom: "0.75px solid black",
                        borderLeft: "0.75px solid black",
                      }}
                    >
                      خوب رو به بالا
                    </div>
                    <div
                      className="w-[120px] h-[22px] leading-[20px] text-center text-[#333] text-[16px] font-bold !font-[B-Nazanin]"
                      style={{
                        borderTop: "0.75px solid black",
                        borderRight: "0.75px solid black",
                        borderBottom: "0.75px solid black",
                        borderLeft: "0.75px solid black",
                      }}
                    >
                      بین 70 تا 80 درصد
                    </div>
                    <div
                      className="w-[120px] h-[22px] leading-[20px] text-center text-[#333] text-[16px] font-bold bg-[#19B200]"
                      style={{
                        borderTop: "0.75px solid black",
                        borderRight: "0.75px solid black",
                        borderBottom: "1.5px solid black",
                        borderLeft: "1.5px solid black",
                      }}
                    ></div>
                  </div>

                  <div className="flex flex-row justify-center items-center h-full w-full">
                    <div
                      className="w-[120px] h-[22px] leading-[20px] text-center text-[#333] text-[16px] font-bold !font-[B-Nazanin]"
                      style={{
                        borderTop: "0.75px solid black",
                        borderRight: "1.5px solid black",
                        borderBottom: "0.75px solid black",
                        borderLeft: "0.75px solid black",
                      }}
                    >
                      خوب
                    </div>
                    <div
                      className="w-[120px] h-[22px] leading-[20px] text-center text-[#333] text-[16px] font-bold !font-[B-Nazanin]"
                      style={{
                        borderTop: "0.75px solid black",
                        borderRight: "0.75px solid black",
                        borderBottom: "0.75px solid black",
                        borderLeft: "0.75px solid black",
                      }}
                    >
                      بین 60 تا 70 درصد
                    </div>
                    <div
                      className="w-[120px] h-[22px] leading-[20px] text-center text-[#333] text-[16px] font-bold bg-[#9AD000]"
                      style={{
                        borderTop: "0.75px solid black",
                        borderRight: "0.75px solid black",
                        borderBottom: "1.5px solid black",
                        borderLeft: "1.5px solid black",
                      }}
                    ></div>
                  </div>

                  <div className="flex flex-row justify-center items-center h-full w-full">
                    <div
                      className="w-[120px] h-[22px] leading-[20px] text-center text-[#333] text-[16px] font-bold !font-[B-Nazanin]"
                      style={{
                        borderTop: "0.75px solid black",
                        borderRight: "1.5px solid black",
                        borderBottom: "0.75px solid black",
                        borderLeft: "0.75px solid black",
                      }}
                    >
                      متوسط رو به بالا
                    </div>
                    <div
                      className="w-[120px] h-[22px] leading-[20px] text-center text-[#333] text-[16px] font-bold !font-[B-Nazanin]"
                      style={{
                        borderTop: "0.75px solid black",
                        borderRight: "0.75px solid black",
                        borderBottom: "0.75px solid black",
                        borderLeft: "0.75px solid black",
                      }}
                    >
                      بین 50 تا 60 درصد
                    </div>
                    <div
                      className="w-[120px] h-[22px] leading-[20px] text-center text-[#333] text-[16px] font-bold bg-[#F2F600]"
                      style={{
                        borderTop: "0.75px solid black",
                        borderRight: "0.75px solid black",
                        borderBottom: "1.5px solid black",
                        borderLeft: "1.5px solid black",
                      }}
                    ></div>
                  </div>

                  <div className="flex flex-row justify-center items-center h-full w-full">
                    <div
                      className="w-[120px] h-[22px] leading-[20px] text-center text-[#333] text-[16px] font-bold !font-[B-Nazanin]"
                      style={{
                        borderTop: "0.75px solid black",
                        borderRight: "1.5px solid black",
                        borderBottom: "0.75px solid black",
                        borderLeft: "0.75px solid black",
                      }}
                    >
                      متوسط
                    </div>
                    <div
                      className="w-[120px] h-[22px] leading-[20px] text-center text-[#333] text-[16px] font-bold !font-[B-Nazanin]"
                      style={{
                        borderTop: "0.75px solid black",
                        borderRight: "0.75px solid black",
                        borderBottom: "0.75px solid black",
                        borderLeft: "0.75px solid black",
                      }}
                    >
                      بین 40 تا 50 درصد
                    </div>
                    <div
                      className="w-[120px] h-[22px] leading-[20px] text-center text-[#333] text-[16px] font-bold bg-[#FFCB12]"
                      style={{
                        borderTop: "0.75px solid black",
                        borderRight: "0.75px solid black",
                        borderBottom: "1.5px solid black",
                        borderLeft: "1.5px solid black",
                      }}
                    ></div>
                  </div>

                  <div className="flex flex-row justify-center items-center h-full w-full">
                    <div
                      className="w-[120px] h-[22px] leading-[20px] text-center text-[#333] text-[16px] font-bold !font-[B-Nazanin]"
                      style={{
                        borderTop: "0.75px solid black",
                        borderRight: "1.5px solid black",
                        borderBottom: "0.75px solid black",
                        borderLeft: "0.75px solid black",
                      }}
                    >
                      ضعیف رو به بالا
                    </div>
                    <div
                      className="w-[120px] h-[22px] leading-[20px] text-center text-[#333] text-[16px] font-bold !font-[B-Nazanin]"
                      style={{
                        borderTop: "0.75px solid black",
                        borderRight: "0.75px solid black",
                        borderBottom: "0.75px solid black",
                        borderLeft: "0.75px solid black",
                      }}
                    >
                      بین 30 تا 40 درصد
                    </div>
                    <div
                      className="w-[120px] h-[22px] leading-[20px] text-center text-[#333] text-[16px] font-bold bg-[#E98C00]"
                      style={{
                        borderTop: "0.75px solid black",
                        borderRight: "0.75px solid black",
                        borderBottom: "1.5px solid black",
                        borderLeft: "1.5px solid black",
                      }}
                    ></div>
                  </div>

                  <div className="flex flex-row justify-center items-center h-full w-full">
                    <div
                      className="w-[120px] h-[22px] leading-[20px] text-center text-[#333] text-[16px] font-bold !font-[B-Nazanin]"
                      style={{
                        borderTop: "0.75px solid black",
                        borderRight: "1.5px solid black",
                        borderBottom: "0.75px solid black",
                        borderLeft: "0.75px solid black",
                      }}
                    >
                      ضعیف
                    </div>
                    <div
                      className="w-[120px] h-[22px] leading-[20px] text-center text-[#333] text-[16px] font-bold !font-[B-Nazanin]"
                      style={{
                        borderTop: "0.75px solid black",
                        borderRight: "0.75px solid black",
                        borderBottom: "0.75px solid black",
                        borderLeft: "0.75px solid black",
                      }}
                    >
                      بین 20 تا 30 درصد
                    </div>
                    <div
                      className="w-[120px] h-[22px] leading-[20px] text-center text-[#333] text-[16px] font-bold bg-[#EE4700]"
                      style={{
                        borderTop: "0.75px solid black",
                        borderRight: "0.75px solid black",
                        borderBottom: "1.5px solid black",
                        borderLeft: "1.5px solid black",
                      }}
                    ></div>
                  </div>

                  <div className="flex flex-row justify-center items-center h-full w-full">
                    <div
                      className="w-[120px] h-[22px] leading-[20px] text-center text-[#333] text-[16px] font-bold !font-[B-Nazanin]"
                      style={{
                        borderTop: "0.75px solid black",
                        borderRight: "1.5px solid black",
                        borderBottom: "1.5px solid black",
                        borderLeft: "0.75px solid black",
                        borderBottomRightRadius: "5px",
                      }}
                    >
                      بسیار ضعیف
                    </div>
                    <div
                      className="w-[120px] h-[22px] leading-[20px] text-center text-[#333] text-[16px] font-bold !font-[B-Nazanin]"
                      style={{
                        borderTop: "0.75px solid black",
                        borderRight: "0.75px solid black",
                        borderBottom: "1.5px solid black",
                        borderLeft: "0.75px solid black",
                      }}
                    >
                      کمتر از 20 درصد
                    </div>
                    <div
                      className="w-[120px] h-[22px] leading-[20px] text-center text-[#333] text-[16px] font-bold bg-[#FF0000]"
                      style={{
                        borderTop: "0.75px solid black",
                        borderRight: "0.75px solid black",
                        borderBottom: "1.5px solid black",
                        borderLeft: "1.5px solid black",
                        borderBottomLeftRadius: "5px",
                      }}
                    ></div>
                  </div>
                </div>
              </div>

              <div className="footer w-full flex flex-col justify-start items-center relative">
                <img
                  className="absolute bottom-0 z-[2]"
                  src={footerImage}
                  width="750px"
                  alt="footer"
                />
                <img
                  className="absolute bottom-2 left-[30px] z-[3]"
                  src={agroyaarPlatform}
                  alt="footer"
                />
                <span className="absolute bottom-[5px] left-[30px] z-[3] text-[12px] font-bold text-[#fff] !font-sans">
                  کد گزارش: {yieldReportQuery?.data?.yieldGap?.yieldGapCode}
                </span>
                <span className="absolute bottom-[5px] right-[15px] z-[3] text-[18px] font-semibold text-[#fff]">
                  {++pageNumber}
                </span>
              </div>
            </section>
          </div>

          {/* <!-- Page4 --> */}
          <div className="page4">
            <section className="flex flex-col justify-between items-center w-full h-[1048px]">
              <img
                className="header"
                src={frame2182}
                alt="header"
                width="750px"
              />

              <div className="flex flex-col justify-start items-center h-full w-full">
                <div className="flex flex-col justify-center items-start w-full px-2">
                  <p className="font-bold text-right text-[#222] text-[22px] !font-[B-Nazanin]">
                    سهم عوامل مختلف در ایجاد خلأ عملکرد
                  </p>
                  <span className="font-semibold text-[16px] !font-[B-Nazanin]">
                    در شکل زیر سهم هر یک از فاکتورهای خلاء عملکرد در مزرعه شما
                    قابل مشاهده است.
                  </span>
                  <span className="font-semibold text-[16px] !font-[B-Nazanin]">
                    خلاء عملکرد مزرعه شما{" "}
                    <span className="text-[#F00]">
                      {categoriesYieldGapContribution?.yieldGapValueInTons} {farmInformation?.product?.productId === 29 ? 'کیلوگرم' : 'تن'}
                    </span>{" "}
                    در هکتار بوده و درصد فاکتورهای دخیل در خلاء عملکرد به شرح
                    زیر است:
                  </span>
                  <br />
                </div>

                <div className="flex flex-row-reverse justify-center items-center w-full">
                  <div className="main-container flex flex-row items-center justify-center">
                    <div className="multivariate-bar-chart flex flex-col items-center justify-center gap-4">
                      <div className="bar-grid-line-guid-option-group flex flex-col items-center justify-center">
                        <div className="bar-grid-line-group flex w-[710px] h-[426px] relative">
                          <div className="Grids-y-axis-value flex flex-row w-[600px] h-[380px] left-6 top-6 absolute">
                            <div className="line-vector-group w-[520px] h-[320px] left-[73px] top-[20px] absolute">
                              <div className="Rectangle2 w-[520px] h-[335px] left-0 top-0 absolute border-[1px] border-dotted border-[#BDBDBD]"></div>

                              <div className="Vector1 w-[520px] h-0 left-0 top-[164px] absolute border-[1px] border-dotted border-[#BDBDBD]"></div>

                              <div className="Vector2 w-[520px] h-0 left-0 top-[200px] absolute border-[1px] border-dotted border-[#BDBDBD]"></div>

                              <div className="Vector3 w-[520px] h-0 left-0 top-[236px] absolute border-[1px] border-dotted border-[#BDBDBD]"></div>

                              <div className="Vector4 w-[520px] h-0 left-0 top-[270px] absolute border-[1px] border-dotted border-[#BDBDBD]"></div>

                              <div className="Vector20 w-[520px] h-0 left-0 top-[31px] absolute border-[1px] border-dotted border-[#BDBDBD]"></div>

                              <div className="Vector21 w-[520px] h-0 left-0 top-[65px] absolute border-[1px] border-dotted border-[#BDBDBD]"></div>

                              <div className="Vector22 w-[520px] h-0 left-0 top-[100px] absolute border-[1px] border-dotted border-[#BDBDBD]"></div>

                              <div className="Vector23 w-[520px] h-0 left-0 top-[133px] absolute border-[1px] border-dotted border-[#BDBDBD]"></div>

                              <div className="Vector19 w-[520px] h-0 left-0 top-[302px] absolute border-[1px] border-dotted border-[#BDBDBD]"></div>
                            </div>

                            <div className="y-axis-value flex flex-col-reverse justify-between top-[45px] right-[33.5rem] h-[320px] absolute text-right text-[#4F4F4F]">
                              {categoriesYieldGapContribution?.barChart?.labels.map(
                                (label, i) => {
                                  return (
                                    <span
                                      key={i}
                                      className="y-axis-value-item whitespace-nowrap text-[16px] font-semibold text-[#4F4F4F] !font-[B-Nazanin] h-fit"
                                    >
                                      {label}
                                    </span>
                                  );
                                }
                              )}
                            </div>

                            <div className="rec-y-label bg-[#4F4F4F]">
                              <p className="chart-title absolute left-[-60px] top-[140px] rotate-[-90deg] text-[20px] font-semibold !font-[B-Nazanin]">
                                {
                                  categoriesYieldGapContribution?.barChart
                                    ?.title
                                }
                              </p>
                            </div>
                          </div>

                          <div className="bar-container flex flex-row justify-between items-end w-[480px] absolute left-[17%] bottom-[2.95rem]">
                            {categoriesYieldGapContribution?.barChart?.y.map(
                              (y, i) => {
                                return (
                                  <div
                                    key={i}
                                    className="bar flex flex-col justify-start items-center rounded-tl-sm w-[20px] relative"
                                    style={{
                                      height: newHeightBarPage4[i] + "px",
                                      background:
                                        "linear-gradient(90deg, #561C93 0%, #6126A1 22%, #6A30A8 47%, #6A30A8 69%, #47088A 100%)",
                                      border: "1px #481083 solid",
                                    }}
                                  >
                                    <span className="y-value absolute top-[-25px] text-[16px] font-semibold text-[#4F4F4F] !font-[B-Nazanin]">
                                      {y}
                                    </span>
                                    <img
                                      src={circle}
                                      alt="circle"
                                      style={{
                                        width: 102,
                                        height: 8,
                                        left: 0,
                                        top: -4,
                                        position: "absolute",
                                        zIndex: 3,
                                      }}
                                    />
                                    <div className="x-value-content text-right absolute bottom-[-50px] left-[-70px] w-[100px] h-[20px] whitespace-nowrap text-[16px] font-semibold rotate-[-30deg] text-[#4F4F4F] !font-[B-Nazanin]">
                                      {
                                        categoriesYieldGapContribution?.barChart
                                          ?.x[i]
                                      }
                                    </div>
                                    <img
                                      src={circlebot}
                                      alt="circle"
                                      style={{
                                        width: "100%",
                                        left: 0,
                                        bottom: -3,
                                        position: "absolute",
                                        zIndex: 3,
                                      }}
                                    />
                                  </div>
                                );
                              }
                            )}
                          </div>
                        </div>
                      </div>

                      <span className="chart-name text-center text-[18px] font-semibold pt-[30px] !font-[B-Nazanin]">
                        {categoriesYieldGapContribution?.barChart?.name}
                      </span>
                    </div>
                  </div>
                </div>

                <div className="flex flex-col justify-center items-start mt-[70px]">
                  <span className="font-bold text-[22px] !font-[B-Nazanin]">
                    راهنما
                  </span>
                  <span className="font-semibold text-[16px] !font-[B-Nazanin]">
                    عواملی که در شکل، درصد خلاء عملکرد بالاتری دارند در واقع سهم
                    بیشتری در کاهش عملکرد داشته و باید در اولویت اصلاح مدیریتی
                    قرار گیرند.
                  </span>
                  <span className="font-semibold text-[16px] !font-[B-Nazanin]">
                    به طور مثال{" "}
                    {
                      categoriesYieldGapContribution?.barChart?.y[
                        categoriesYieldGapContribution?.barChart?.y?.length - 1
                      ]
                    }{" "}
                    درصد از خلاء عملکرد زمین{" "}
                    {farmInformation?.product?.persianName} شما مربوط به{" "}
                    {
                      categoriesYieldGapContribution?.barChart?.x[
                        categoriesYieldGapContribution?.barChart?.x?.length - 1
                      ]
                    }{" "}
                    است.
                  </span>
                </div>
              </div>

              <div className="footer w-full flex flex-col justify-start items-center relative">
                <img
                  className="absolute bottom-0 z-[2]"
                  src={footerImage}
                  width="750px"
                  alt="footer"
                />
                <img
                  className="absolute bottom-2 left-[30px] z-[3]"
                  src={agroyaarPlatform}
                  alt="footer"
                />
                <span className="absolute bottom-[5px] left-[30px] z-[3] text-[12px] font-bold text-[#fff] !font-sans">
                  کد گزارش: {yieldReportQuery?.data?.yieldGap?.yieldGapCode}
                </span>
                <span className="absolute bottom-[5px] right-[15px] z-[3] text-[18px] font-semibold text-[#fff]">
                  {++pageNumber}
                </span>
              </div>
            </section>
          </div>

          {/* <!-- Page5 --> */}
          <div className="page5">
            <section className="flex flex-col justify-between items-center w-full h-[1048px]">
              <img
                className="header"
                src={frame2182}
                alt="header"
                width="750px"
              />

              <div className="flex flex-col justify-start items-center h-full w-full mt-[-10px] gap-[10px]">
                <div className="flex flex-col justify-center items-start w-full px-2">
                  <p className="font-bold text-right text-[#222] text-[22px] !font-[B-Nazanin]">
                    {yieldGapValue?.headLine}
                  </p>
                  <span className="font-semibold text-[16px] !font-[B-Nazanin]">
                    در شکل زیر {yieldGapValue?.headLine} مزرعه شما مشاهده
                    می‌شود. قسمت بنفش پررنگ در شکل، عملکرد بدست آمده و قسمت بنفش
                    کم‌رنگ عملکرد ازدست رفته را نشان می‌دهد.
                  </span>
                  <br />
                </div>

                {yieldGapValue?.charts?.map((item, i) => {
                  newHeightBarPage5 = [];
                  item?.y?.forEach((height) =>
                    newHeightBarPage5.push(
                      (height * 165) / item?.labels[item?.labels?.length - 1]
                    )
                  );
                  return (
                    <div
                      key={i}
                      className="main-container flex flex-row-reverse items-center justify-center gap-8"
                    >
                      <div className="multivariate-bar-chart flex flex-col items-center justify-center">
                        <div className="bar-grid-line-guid-option-group flex flex-col items-center justify-center">
                          <div className="bar-grid-line-group flex w-[368.5px] h-[234.3px] relative">
                            <div className="Grids-y-axis-value w-[330px] h-[209px] left-[13.2px] top-[13.2px] absolute">
                              <div className="line-vector-group w-[286px] h-[176px] left-[40.15px] top-[11px] absolute">
                                <div className="Rectangle2 w-[310px] h-[184.25px] left-0 top-0 absolute border-[1px] border-dotted border-[#BDBDBD]"></div>

                                <div className="Vector-horizontal w-[310px] h-0 left-0 top-[90.2px] absolute border-[1px] border-dotted border-[#BDBDBD]"></div>

                                <div className="Vector-horizontal w-[310px] h-0 left-0 top-[110px] absolute border-[1px] border-dotted border-[#BDBDBD]"></div>

                                <div className="Vector-horizontal w-[310px] h-0 left-0 top-[129.8px] absolute border-[1px] border-dotted border-[#BDBDBD]"></div>

                                <div className="Vector-horizontal w-[310px] h-0 left-0 top-[148.5px] absolute border-[1px] border-dotted border-[#BDBDBD]"></div>

                                <div className="Vector-horizontal w-[310px] h-0 left-0 top-[17.05px] absolute border-[1px] border-dotted border-[#BDBDBD]"></div>

                                <div className="Vector-horizontal w-[310px] h-0 left-0 top-[35.75px] absolute border-[1px] border-dotted border-[#BDBDBD]"></div>

                                <div className="Vector-horizontal w-[310px] h-0 left-0 top-[55px] absolute border-[1px] border-dotted border-[#BDBDBD]"></div>

                                <div className="Vector-horizontal w-[310px] h-0 left-0 top-[73.15px] absolute border-[1px] border-dotted border-[#BDBDBD]"></div>

                                <div className="Vector-horizontal w-[310px] h-0 left-0 top-[166.1px] absolute border-[1px] border-dotted border-[#BDBDBD]"></div>

                                <div className="Vector-vertical w-[184.25px] h-0 left-[77.5px] top-0 absolute rotate-[90deg] origin-[0] border-[1px] border-dotted border-[#BDBDBD]"></div>

                                <div className="Vector-vertical w-[184.25px] h-0 left-[155px] top-0 absolute rotate-[90deg] origin-[0] border-[1px] border-dotted border-[#BDBDBD]"></div>

                                <div className="Vector-vertical w-[184.25px] h-0 left-[232.5px] top-0 absolute rotate-[90deg] origin-[0] border-[1px] border-dotted border-[#BDBDBD]"></div>
                              </div>

                              <div className="y-axis-value flex flex-col-reverse justify-between top-[2.2px] right-[18.7rem] h-[203.5px] absolute text-right text-[#4F4F4F]">
                                {item?.labels?.map((label, i) => {
                                  return (
                                    <span
                                      key={i}
                                      className="y-axis-value-item text-right text-[#4F4F4F] text-[16px] font-semibold !font-[B-Nazanin] h-fit"
                                    >
                                      {label}
                                    </span>
                                  );
                                })}
                              </div>

                              <p className="chart-title absolute left-[-110px] top-[77px] transform: rotate-[-90deg] text-[16px] font-semibold !font-[B-Nazanin]">
                                {item?.title}
                              </p>
                            </div>

                            <div className="bar-container absolute bottom-[35.2px] left-[77px]">
                              <div className="piece-bar-container flex flex-col items-end">
                                {item?.y?.map((y, i) => {
                                  return (
                                    <div
                                      key={i}
                                      className="flex flex-row items-center"
                                    >
                                      <p
                                        className={`explaintion-piece-bar text-right ${
                                          y === 0 && "hidden"
                                        } text-[12px] font-semibold !font-[B-Nazanin]`}
                                      >
                                        {item?.x[i]}
                                      </p>
                                      <div
                                        className={`guid-line-connector h-[0.5px] ${
                                          y === 0 && "hidden"
                                        } w-[17.5px] mr-[2.5px] bg-[black]`}
                                      ></div>
                                      <div
                                        className={`Group4044 ${
                                          y === 0 && "hidden"
                                        }`}
                                        style={{
                                          width: 95,
                                          height: newHeightBarPage5[i] + "px",
                                          position: "relative",
                                          zIndex: 2,
                                          opacity: item?.x[i]?.includes(
                                            "خلأ عملکرد"
                                          )
                                            ? 0.7
                                            : 1,
                                          background:
                                            "linear-gradient(90deg, #460F81 0%, #6328A3 20%, #6C32AA 50%, #6429A4 77%, #460F81 100%)",
                                        }}
                                      >
                                        <img
                                          className={`top-circle ${
                                            y === 0 && "hidden"
                                          }`}
                                          src={circle}
                                          alt="top-circle"
                                          style={{
                                            width: 102,
                                            height: 20,
                                            left: 0,
                                            top: -10,
                                            position: "absolute",
                                            zIndex: 3,
                                          }}
                                        />
                                        <img
                                          className={`bottom-circle ${
                                            y === 0 && "hidden"
                                          }`}
                                          src={circlebot}
                                          alt="bottom-circle"
                                          style={{
                                            width: 102,
                                            left: 0,
                                            bottom: -12,
                                            position: "absolute",
                                            zIndex: 0,
                                          }}
                                        />
                                      </div>
                                    </div>
                                  );
                                })}
                              </div>
                            </div>
                          </div>

                          <span className="chart-name text-center text-[16px] font-semibold !font-[B-Nazanin]">
                            {item?.name}
                          </span>
                        </div>
                      </div>

                      <div className="guid-container flex flex-col items-start justify-center gap-[17px] translate-x-0 translate-y-[15px]">
                        <span
                          className="text-center text-[20px] font-extrabold !font-[B-Nazanin]"
                          style={{ borderBottom: "1.5px solid #4F4F4F" }}
                        >
                          راهنما
                        </span>

                        <div className="guid-elipse-container flex flex-row">
                          <div className="elipse-container flex flex-col items-start justify-center">
                            {item?.legend?.map((legend, i) => {
                              return (
                                <div className="guid-option flex flex-row-reverse items-center justify-end gap-2 text-right h-[35px] text-[16px] font-semibold text-[black] !font-[B-Nazanin]">
                                  {legend}
                                  <div
                                    className="elipse-guid-line-connector rounded-full h-[6.5px] w-[6.5px]"
                                    style={{
                                      backgroundColor: item?.colors[0],
                                      opacity: item?.x[i]?.includes(
                                        "خلأ عملکرد"
                                      )
                                        ? 0.7
                                        : 1,
                                    }}
                                  ></div>
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })}

                <div className="flex flex-col justify-center items-start w-full gap-[2px] px-2">
                  <span className="font-bold text-[24px] !font-[B-Nazanin]">
                    راهنما
                  </span>
                  <span className="pr-[10px] !font-[B-Nazanin]">
                    <span className="font-semibold text-[18px] !font-[B-Nazanin]">
                      عملکرد واقعی :
                    </span>{" "}
                    عملکردی که توسط کشاورز برداشت می‌شود.
                  </span>
                  <span className="pr-[10px] !font-[B-Nazanin]">
                    <span className="font-semibold text-[18px] !font-[B-Nazanin]">
                      خلاء عملکرد :
                    </span>{" "}
                    فاصله بین عملکرد برداشت شده تا پتانسیل عملکرد است.
                  </span>
                </div>
              </div>

              <div className="footer w-full flex flex-col justify-start items-center relative">
                <img
                  className="absolute bottom-0 z-[2]"
                  src={footerImage}
                  width="750px"
                  alt="footer"
                />
                <img
                  className="absolute bottom-2 left-[30px] z-[3]"
                  src={agroyaarPlatform}
                  alt="footer"
                />
                <span className="absolute bottom-[5px] left-[30px] z-[3] text-[12px] font-bold text-[#fff] !font-sans">
                  کد گزارش: {yieldReportQuery?.data?.yieldGap?.yieldGapCode}
                </span>
                <span className="absolute bottom-[5px] right-[15px] z-[3] text-[18px] font-semibold text-[#fff]">
                  {++pageNumber}
                </span>
              </div>
            </section>
          </div>

          {/* <!-- Page6 --> */}
          <div className="page6">
            <section className="flex flex-col justify-between items-center w-full h-[1048px]">
              <img
                className="header"
                src={frame2182}
                alt="header"
                width="750px"
              />

              <div className="flex flex-col justify-start items-center h-full w-full mt-[-10px] gap-[10px]">
                <div className="flex flex-col justify-center items-start w-full px-2">
                  <p className="font-bold text-right text-[#222] text-[22px] !font-[B-Nazanin]">
                    {lostEconomicValueAmount?.headLine}
                  </p>
                  <span className="font-semibold text-[16px] !font-[B-Nazanin]">
                    در شکل زیر {lostEconomicValueAmount?.headLine} مزرعه شما
                    مشاهده می‌شود. قسمت بنفش پررنگ در شکل، درآمد حاصل از فروش و
                    قسمت بنفش کم‌رنگ ارزش اقتصادی از دست رفته را نشان می‌دهد.
                  </span>
                  <br />
                </div>

                {lostEconomicValueAmount?.charts?.map((item, i) => {
                  newHeightBarPage6 = [];
                  item?.y?.forEach((height) =>
                    newHeightBarPage6.push(
                      (height * 165) / item?.labels[item?.labels?.length - 1]
                    )
                  );
                  return (
                    <div
                      key={i}
                      className="main-container self-start flex flex-row-reverse items-center justify-center gap-2 px-4"
                    >
                      <div className="multivariate-bar-chart flex flex-col items-center justify-center">
                        <div className="bar-grid-line-guid-option-group flex flex-col items-center justify-center">
                          <div className="bar-grid-line-group flex w-[368.5px] h-[234.3px] relative">
                            <div className="Grids-y-axis-value w-[330px] h-[209px] left-[13.2px] top-[13.2px] absolute">
                              <div className="line-vector-group w-[286px] h-[176px] left-[40.15px] top-[11px] absolute">
                                <div className="Rectangle2 w-[310px] h-[184.25px] left-0 top-0 absolute border-[1px] border-dotted border-[#BDBDBD]"></div>

                                <div className="Vector-horizontal w-[310px] h-0 left-0 top-[90.2px] absolute border-[1px] border-dotted border-[#BDBDBD]"></div>

                                <div className="Vector-horizontal w-[310px] h-0 left-0 top-[110px] absolute border-[1px] border-dotted border-[#BDBDBD]"></div>

                                <div className="Vector-horizontal w-[310px] h-0 left-0 top-[129.8px] absolute border-[1px] border-dotted border-[#BDBDBD]"></div>

                                <div className="Vector-horizontal w-[310px] h-0 left-0 top-[148.5px] absolute border-[1px] border-dotted border-[#BDBDBD]"></div>

                                <div className="Vector-horizontal w-[310px] h-0 left-0 top-[17.05px] absolute border-[1px] border-dotted border-[#BDBDBD]"></div>

                                <div className="Vector-horizontal w-[310px] h-0 left-0 top-[35.75px] absolute border-[1px] border-dotted border-[#BDBDBD]"></div>

                                <div className="Vector-horizontal w-[310px] h-0 left-0 top-[55px] absolute border-[1px] border-dotted border-[#BDBDBD]"></div>

                                <div className="Vector-horizontal w-[310px] h-0 left-0 top-[73.15px] absolute border-[1px] border-dotted border-[#BDBDBD]"></div>

                                <div className="Vector-horizontal w-[310px] h-0 left-0 top-[166.1px] absolute border-[1px] border-dotted border-[#BDBDBD]"></div>

                                <div className="Vector-vertical w-[184.25px] h-0 left-[77.5px] top-0 absolute rotate-[90deg] origin-[0] border-[1px] border-dotted border-[#BDBDBD]"></div>

                                <div className="Vector-vertical w-[184.25px] h-0 left-[155px] top-0 absolute rotate-[90deg] origin-[0] border-[1px] border-dotted border-[#BDBDBD]"></div>

                                <div className="Vector-vertical w-[184.25px] h-0 left-[232.5px] top-0 absolute rotate-[90deg] origin-[0] border-[1px] border-dotted border-[#BDBDBD]"></div>
                              </div>

                              <div className="y-axis-value flex flex-col-reverse justify-between top-[2.2px] right-[18.7rem] h-[203.5px] absolute text-right text-[#4F4F4F]">
                                {item?.labels?.map((label, i) => {
                                  return (
                                    <span
                                      key={i}
                                      className="y-axis-value-item text-right text-[#4F4F4F] text-[16px] font-semibold !font-[B-Nazanin] h-fit"
                                    >
                                      {item?.title?.includes("ریال در هکتار")
                                        ? label
                                            ?.toString()
                                            ?.replace(
                                              /\B(?=(\d{3})+(?!\d))/g,
                                              ","
                                            )
                                        : label}
                                    </span>
                                  );
                                })}
                              </div>

                              <p className="chart-title absolute h-[200px] left-[-100px] [writing-mode:vertical-lr] rotate-180 text-[16px] font-semibold !font-[B-Nazanin]">
                                {item?.title}
                              </p>
                            </div>

                            <div className="bar-container absolute bottom-[35.2px] left-[77px]">
                              <div className="piece-bar-container flex flex-col items-end">
                                {item?.y?.map((y, i) => {
                                  return (
                                    <div
                                      key={i}
                                      className="flex flex-row items-center"
                                    >
                                      <p
                                        className={`explaintion-piece-bar ${
                                          y === 0 && "hidden"
                                        } text-right text-[12px] font-semibold !font-[B-Nazanin]`}
                                      >
                                        {item?.x[i]}
                                      </p>
                                      <div
                                        className={`guid-line-connector h-[0.5px] ${
                                          y === 0 && "hidden"
                                        } w-[17.5px] mr-[2.5px] bg-[black]`}
                                      ></div>
                                      <div
                                        className={`Group4044 ${
                                          y === 0 && "hidden"
                                        }`}
                                        style={{
                                          width: 95,
                                          height: newHeightBarPage5[i] + "px",
                                          position: "relative",
                                          zIndex: 2,
                                          opacity: item?.x[i]?.includes(
                                            "ارزش اقتصادی از دست رفته"
                                          )
                                            ? 0.7
                                            : 1,
                                          background:
                                            "linear-gradient(90deg, #460F81 0%, #6328A3 20%, #6C32AA 50%, #6429A4 77%, #460F81 100%)",
                                        }}
                                      >
                                        <img
                                          className={`top-circle ${
                                            y === 0 && "hidden"
                                          }`}
                                          src={circle}
                                          alt="top-circle"
                                          style={{
                                            width: "100%",
                                            height: 20,
                                            left: 0,
                                            top: -10,
                                            position: "absolute",
                                            zIndex: 3,
                                          }}
                                        />
                                        <img
                                          className={`bottom-circle ${
                                            y === 0 && "hidden"
                                          }`}
                                          src={circlebot}
                                          alt="bottom-circle"
                                          style={{
                                            width: "100%",
                                            left: 0,
                                            bottom: -12,
                                            position: "absolute",
                                            zIndex: 0,
                                          }}
                                        />
                                      </div>
                                    </div>
                                  );
                                })}
                              </div>
                            </div>
                          </div>

                          <span className="chart-name text-center text-[16px] font-semibold !font-[B-Nazanin]">
                            {item?.name}
                          </span>
                        </div>
                      </div>

                      <div className="guid-container flex flex-col items-start justify-center gap-[17px] translate-x-0 translate-y-[15px]">
                        <span
                          className="text-start text-[20px] font-extrabold !font-[B-Nazanin]"
                          style={{ borderBottom: "1.5px solid #4F4F4F" }}
                        >
                          راهنما
                        </span>

                        <div className="guid-elipse-container flex flex-row">
                          <div className="elipse-container flex flex-col items-start">
                            {item?.legend?.map((legend, i) => {
                              return (
                                <div
                                  key={i}
                                  className="guid-option flex flex-row-reverse items-center justify-end gap-2 text-right h-[35px] w-[200px] text-[14px] font-semibold text-[black] !font-[B-Nazanin]"
                                >
                                  {legend}
                                  <div
                                    className="elipse-guid-line-connector rounded-full h-[6.5px] w-[6.5px]"
                                    style={{
                                      backgroundColor: item?.colors[0],
                                      opacity: item?.x[i]?.includes(
                                        "ارزش اقتصادی از دست رفته"
                                      )
                                        ? 0.7
                                        : 1,
                                    }}
                                  ></div>
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })}

                <div className="flex flex-col justify-center items-start w-full mt-9 px-2">
                  <p className="font-bold text-[22px] !font-[B-Nazanin]">
                    راهنما
                  </p>
                  <span className="pr-[10px] !font-[B-Nazanin]">
                    <span className="font-semibold text-[18px] !font-[B-Nazanin]">
                      ارزش اقتصادی از دست رفته:
                    </span>{" "}
                    میزان کاهش درآمد به علت کاهش عملکرد می‌باشد.
                  </span>
                </div>
              </div>

              <div className="footer w-full flex flex-col justify-start items-center relative">
                <img
                  className="absolute bottom-0 z-[2]"
                  src={footerImage}
                  width="750px"
                  alt="footer"
                />
                <img
                  className="absolute bottom-2 left-[30px] z-[3]"
                  src={agroyaarPlatform}
                  alt="footer"
                />
                <span className="absolute bottom-[5px] left-[30px] z-[3] text-[12px] font-bold text-[#fff] !font-sans">
                  کد گزارش: {yieldReportQuery?.data?.yieldGap?.yieldGapCode}
                </span>
                <span className="absolute bottom-[5px] right-[15px] z-[3] text-[18px] font-semibold text-[#fff]">
                  {++pageNumber}
                </span>
              </div>
            </section>
          </div>

          {/* <!-- Page7 --> */}
          <div className="page7">
            <section className="flex flex-col justify-between items-center w-full h-[1048px]">
              <img
                className="header"
                src={frame2182}
                alt="header"
                width="750px"
              />

              <div className="flex flex-col justify-start items-center h-full w-full gap-[20px]">
                <div className="flex flex-col justify-center items-start w-full px-2">
                  <p className="font-bold text-[22px] text-[#222] !font-[B-Nazanin]">
                    سهم زیر مجموعه‌های عوامل ایجادکننده خلأ عملکرد
                  </p>
                  <span className="leading-[25px] font-semibold text-[16px] !font-[B-Nazanin]">
                    جدول زیر سهم عوامل مختلف در کاهش عملکرد مزرعه شما را نشان
                    می‌دهد. عواملی که با رنگ قرمز مشخص شده اند نقش بیشتری در
                    کاهش عملکرد داشته و عواملی که با رنگ سبز مشخص شده‌اند نقش
                    کمتری در کاهش عملکرد داشته‌اند. در ادامه گزارش سهم
                    زیرمجموعه‌های عوامل ایجادکننده خلأ عملکرد بررسی خواهند شد.
                  </span>
                  <br />
                </div>

                <div className="flex flex-col justify-start items-start w-full mt-3 px-2">
                  <span className="font-semibold text-[18px] !font-[B-Nazanin]">
                    جدول 2- سهم عوامل مختلف در ایجاد خلأ عملکرد (درصد)
                  </span>
                </div>

                <div className="flex flex-row justify-center items-center">
                  <div className="flex flex-col justify-center items-center">
                    {parametersYieldGapContribution[0]?.map((item, i) => {
                      const cell1Style = {
                        borderTopRightRadius: i === 0 && "5px",
                        borderBottomRightRadius:
                          i === parametersYieldGapContribution[0]?.length - 1 &&
                          "5px",
                        borderTop:
                          i === 0 ? "1.5px solid black" : "0.75px solid black",
                        borderRight: "1.5px solid black",
                        borderBottom:
                          i === parametersYieldGapContribution[0]?.length - 1
                            ? "1.5px solid black"
                            : "0.75px solid black",
                        borderLeft: "0.75px solid black",
                      };
                      const cell2Style = {
                        borderTop:
                          i === 0 ? "1.5px solid black" : "0.75px solid black",
                        borderRight: "0.75px solid black",
                        borderBottom:
                          i === parametersYieldGapContribution[0]?.length - 1
                            ? "1.5px solid black"
                            : "0.75px solid black",
                        borderLeft: "0.75px solid black",
                        backgroundColor: tableYieldGapLegendHelper(
                          item?.value,
                          parametersYieldGapContribution
                        ),
                      };
                      return (
                        <div
                          key={i}
                          className="flex flex-row justify-center items-center h-full w-full"
                        >
                          <div
                            className="w-[180px] h-[25px] leading-6 text-center text-[#333] text-[16px] font-bold !font-[B-Nazanin]"
                            style={cell1Style}
                          >
                            {item?.title}
                          </div>
                          <div
                            className="w-[90px] h-[25px] leading-6 text-center text-[#000] text-[16px] font-bold !font-[B-Nazanin]"
                            style={cell2Style}
                          >
                            {item?.value}
                          </div>
                        </div>
                      );
                    })}
                  </div>

                  <div className="flex flex-col justify-center items-center">
                    {parametersYieldGapContribution[1]?.map((item, i) => {
                      const cell1Style = {
                        borderTop:
                          i === 0 ? "1.5px solid black" : "0.75px solid black",
                        borderRight: "0.75px solid black",
                        borderBottom:
                          i === parametersYieldGapContribution[1]?.length - 1
                            ? "1.5px solid black"
                            : "0.75px solid black",
                        borderLeft: "0.75px solid black",
                      };
                      const cell2Style = {
                        borderTopLeftRadius: i === 0 && "5px",
                        borderBottomLeftRadius:
                          i === parametersYieldGapContribution[1]?.length - 1 &&
                          "5px",
                        borderTop:
                          i === 0 ? "1.5px solid black" : "0.75px solid black",
                        borderRight: "0.75px solid black",
                        borderBottom:
                          i === parametersYieldGapContribution[1]?.length - 1
                            ? "1.5px solid black"
                            : "0.75px solid black",
                        borderLeft: "1.5px solid black",
                        backgroundColor: tableYieldGapLegendHelper(
                          item?.value,
                          parametersYieldGapContribution
                        ),
                      };
                      return (
                        <div
                          key={i}
                          className="flex flex-row justify-center items-center h-full w-full"
                        >
                          <div
                            className="w-[180px] h-[25px] leading-6 text-center text-[#333] text-[16px] font-bold !font-[B-Nazanin]"
                            style={cell1Style}
                          >
                            {item?.title}
                          </div>
                          <div
                            className="w-[90px] h-[25px] leading-6 text-center text-[#000] text-[16px] font-bold !font-[B-Nazanin]"
                            style={cell2Style}
                          >
                            {item?.value}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>

                <div className="flex flex-col justify-center items-start w-full mt-7 px-2">
                  <p className="text-right text-[black] text-[22px] font-bold !font-[B-Nazanin]">
                    راهنما
                  </p>
                  <span className="leading-[25px] text-[18px] font-semibold text-right text-[black] !font-[B-Nazanin]">
                    ارزش طیف رنگی خلاء عملکرد در جدول از سبز تا قرمز می‌باشد.
                    رنگ قرمز به معنی اثر بیشتر بر کاهش عملکرد بوده و رنگ سبز به
                    معنی اثر کمتر بر کاهش عملکرد می‌باشد.
                  </span>
                </div>

                <img src={table} alt="table" width="240px" height="204px" />
              </div>

              <div className="footer w-full flex flex-col justify-start items-center relative">
                <img
                  className="absolute bottom-0 z-[2]"
                  src={footerImage}
                  width="750px"
                  alt="footer"
                />
                <img
                  className="absolute bottom-2 left-[30px] z-[3]"
                  src={agroyaarPlatform}
                  alt="footer"
                />
                <span className="absolute bottom-[5px] left-[30px] z-[3] text-[12px] font-bold text-[#fff] !font-sans">
                  کد گزارش: {yieldReportQuery?.data?.yieldGap?.yieldGapCode}
                </span>
                <span className="absolute bottom-[5px] right-[15px] z-[3] text-[18px] font-semibold text-[#fff]">
                  {++pageNumber}
                </span>
              </div>
            </section>
          </div>

          {/* <!-- Page8 --> */}
          <div className="page8">
            {parametersYieldGapContributionChart?.map((page, i) => {
              // filling out the array with legends that its y value is zero
              zeroYvalueLegend = [];
              page?.charts[0]?.y?.forEach((value, i) => {
                if (value === 0) {
                  zeroYvalueLegend.push(page?.charts[0]?.legend[i]);
                }
              });
              return (
                <section
                  key={i}
                  className="flex flex-col justify-between items-center w-full h-[1048px]"
                >
                  <img
                    className="header"
                    src={frame2182}
                    alt="header"
                    width="750px"
                  />

                  <div className="flex flex-col justify-start items-center h-full w-full">
                    <div className="flex flex-col justify-center items-start w-full leading-[40px] px-2">
                      <span className="font-bold text-right text-[#222] text-[25px] !font-[B-Nazanin]">
                        {page?.headLine}
                      </span>
                      <span className="leading-[25px] text-[18px] font-semibold !font-[B-Nazanin]">
                        شکل زیر سهم {page?.parameters} را در کاهش عملکرد نشان
                        می‌دهد. هر عاملی که سهم بیشتری در کاهش عملکرد داشته
                        باشد، بخش بزرگتری از ستون را به خود اختصاص می‌دهد.
                      </span>
                      <br />
                    </div>

                    {page?.charts?.map((item, i) => {
                      sumHeightPage8 = 0;
                      newHeightBarPage8 = [];
                      item?.y?.forEach((height) => (sumHeightPage8 += height));
                      item?.y?.forEach((height) =>
                        newHeightBarPage8.push(
                          (height * 300) /
                            item?.labels[item?.labels?.length - 1]
                        )
                      );
                      return (
                        <div
                          key={i}
                          className="main-container flex flex-row-reverse items-center justify-center"
                        >
                          <div className="bar-grid-line-guid-option-group flex flex-col items-center justify-center">
                            <div className="bar-grid-line-group flex w-[520px] h-[426px] relative">
                              <div className="Grids-y-axis-value w-[520px] h-[380px] left-6 top-6 absolute">
                                <div className="line-vector-group w-[400px] h-[335px] left-[73px] top-[20px] absolute">
                                  <div className="Rectangle2 w-[400px] h-[335px] left-0 top-0 absolute border-[1px] border-dotted border-[#BDBDBD]"></div>

                                  <div className="Vector-horizontal w-[400px] h-0 left-0 top-[164px] absolute border-[1px] border-dotted border-[#BDBDBD]"></div>

                                  <div className="Vector-horizontal w-[400px] h-0 left-0 top-[200px] absolute border-[1px] border-dotted border-[#BDBDBD]"></div>

                                  <div className="Vector-horizontal w-[400px] h-0 left-0 top-[236px] absolute border-[1px] border-dotted border-[#BDBDBD]"></div>

                                  <div className="Vector-horizontal w-[400px] h-0 left-0 top-[270px] absolute border-[1px] border-dotted border-[#BDBDBD]"></div>

                                  <div className="Vector-horizontal w-[400px] h-0 left-0 top-[31px] absolute border-[1px] border-dotted border-[#BDBDBD]"></div>

                                  <div className="Vector-horizontal w-[400px] h-0 left-0 top-[65px] absolute border-[1px] border-dotted border-[#BDBDBD]"></div>

                                  <div className="Vector-horizontal w-[400px] h-0 left-0 top-[100px] absolute border-[1px] border-dotted border-[#BDBDBD]"></div>

                                  <div className="Vector-horizontal w-[400px] h-0 left-0 top-[133px] absolute border-[1px] border-dotted border-[#BDBDBD]"></div>

                                  <div className="Vector-horizontal w-[400px] h-0 left-0 top-[302px] absolute border-[1px] border-dotted border-[#BDBDBD]"></div>

                                  <div className="Vector-vertical w-[335px] h-0 left-[123px] top-0 absolute rotate-[90deg] origin-[0] border-[1px] border-dotted border-[#BDBDBD]"></div>

                                  <div className="Vector-vertical w-[335px] h-0 left-[260px] top-0 absolute rotate-[90deg] origin-[0] border-[1px] border-dotted border-[#BDBDBD]"></div>
                                </div>

                                <div
                                  class="y-axis-value"
                                  className="y-axis-value flex flex-col-reverse justify-between top-[40px] right-[29rem] h-[320px] absolute text-right text-[#4F4F4F]"
                                >
                                  {item?.labels?.map((label, i) => {
                                    return (
                                      <span
                                        key={i}
                                        className="y-axis-value-item text-[#4F4F4F] text-right text-[20px] font-semibold !font-[B-Nazanin] h-[20px]"
                                      >
                                        {label}
                                      </span>
                                    );
                                  })}
                                </div>

                                <div className="rec-y-label bg-[#4F4F4F]">
                                  <p className="chart-title absolute left-[-75px] top-[140px] rotate-[-90deg] text-[20px] font-semibold !font-[B-Nazanin]">
                                    {item?.title}
                                  </p>
                                </div>
                              </div>

                              <div className="bar-container absolute left-[25%] bottom-[2.97rem]">
                                <div className="piece-bar-container flex flex-col items-end">
                                  {item?.y?.map((y, i) => {
                                    return (
                                      <div
                                        key={i}
                                        className="flex flex-row items-center"
                                        style={{
                                          height: newHeightBarPage8[i] + "px",
                                        }}
                                      >
                                        <p
                                          className={`explaintion-piece-bar text-right ${
                                            y === 0 && "hidden"
                                          } text-[16px] font-semibold !font-[B-Nazanin]`}
                                        >
                                          {item?.x[i]}
                                        </p>
                                        <div
                                          className={`guid-line-connector h-[0.5px] ${
                                            y === 0 && "hidden"
                                          } w-[17.5px] mr-[2.5px] bg-[black]`}
                                        ></div>
                                        <div
                                          className={`bar-piece w-[150px] ${
                                            y === 0 && "hidden"
                                          }`}
                                          style={{
                                            height: newHeightBarPage8[i] + "px",
                                            backgroundColor: item?.colors[i],
                                          }}
                                        ></div>
                                      </div>
                                    );
                                  })}
                                </div>
                              </div>
                            </div>

                            <span className="chart-name text-center text-[20px] font-semibold !font-[B-Nazanin]">
                              {item?.name}
                            </span>
                          </div>

                          <div className="guid-container flex flex-col items-start gap-[17px] translate-x-0 translate-y-[20px]">
                            <span
                              className="text-center text-[24px] font-extrabold !font-[B-Nazanin]"
                              style={{ borderBottom: "1.5px solid #4F4F4F" }}
                            >
                              راهنما
                            </span>

                            <div className="guid-elipse-container flex flex-col items-start gap-2">
                              {item?.legend?.map((legend, i) => {
                                return (
                                  <div
                                    key={i}
                                    className="guid-option flex flex-row items-center gap-2"
                                  >
                                    <div
                                      className="elipse-guid-line-connector rounded-full h-[13px] w-[13px]"
                                      style={{
                                        backgroundColor: item?.colors[i],
                                      }}
                                    ></div>
                                    <span className="text-right text-[16px] font-semibold text-[black] !font-[B-Nazanin]">
                                      {legend}
                                    </span>
                                  </div>
                                );
                              })}
                            </div>
                          </div>
                        </div>
                      );
                    })}

                    <div className="flex flex-col justify-center items-start w-full mt-9 px-2">
                      <p className="font-bold text-[22px] !font-[B-Nazanin]">
                        راهنما
                      </p>
                      <span className="font-semibold text-[18px] pr-[10px] !font-[B-Nazanin]">
                        در شکل بالا سهم خلأ عملکرد هر یک از پارامتر‌های موثر در{" "}
                        {page?.headLine} نمایش داده شده است.
                      </span>
                      {zeroYvalueLegend.length !== 0 && (
                        <span className="font-semibold text-[18px] pr-[10px] !font-[B-Nazanin]">
                          {zeroYvalueLegend.map((value, i) => value + "، ")}{" "}
                          مدیریت قابل قبولی داشته و تأثیر چندانی در خلأ عملکرد
                          نداشته است.
                        </span>
                      )}
                    </div>
                  </div>

                  <div className="footer w-full flex flex-col justify-start items-center relative">
                    <img
                      className="absolute bottom-0 z-[2]"
                      src={footerImage}
                      width="750px"
                      alt="footer"
                    />
                    <img
                      className="absolute bottom-2 left-[30px] z-[3]"
                      src={agroyaarPlatform}
                      alt="footer"
                    />
                    <span className="absolute bottom-[5px] left-[30px] z-[3] text-[12px] font-bold text-[#fff] !font-sans">
                      کد گزارش: {yieldReportQuery?.data?.yieldGap?.yieldGapCode}
                    </span>
                    <span className="absolute bottom-[5px] right-[15px] z-[3] text-[18px] font-semibold text-[#fff]">
                      {++pageNumber}
                    </span>
                  </div>
                </section>
              );
            })}
          </div>

          <div className="flex flex-col">
            <Button
              className="button-neo h-fit !text-[#333] hover:brightness-105 fixed top-[20px] right-[100px] max-lg:right-[32px] px-8 text-[18px]  w-[150px]"
              id="download-button"
              onClick={() => window.print()}
            >
              <p>پرینت گزارش</p>
            </Button>
            <Button
              className="button-neo h-fit !text-[#333] hover:brightness-105 fixed top-[65px] right-[100px] max-lg:right-[32px] px-8 text-[18px] w-[150px]"
              id="download-button"
              onClick={() => window.close()}
            >
              <p> بازگشت</p>
            </Button>
            <div
              id="responsive-report"
              className="hidden max-lg:flex animate-pulse p-3 bg-[#FBF199] w-[320px] justify-start items-center fixed top-[125px] right-[32px] gap-1 -mb-6 rounded-xl"
            >
              <WarningOutlined className="text-red-600 text-lg" />
              <div className="text-right text-[#111111] max-lg:text-xs">
                لطفا برای دریافت گزارش در اندازه‌های مناسب‌تر، از رایانه شخصی
                استفاده کنید.
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

export default YieldGapReport;
